// Doxycycline for malaria prophylaxis is entirely off-label. The
// schedule has offLabel: true with the rationale provided. Single brand,
// single flat dose (100 mg daily, age 12 and over, no weight banding).
// 28-day post-travel tail.

import { registerTier2Config } from "../tier2ConfigRegistry";
import { PGD_IDS } from "../../pgds/pgdIds";

registerTier2Config({
  pgdId: PGD_IDS.DOXYCYCLINE,
  displayName: "Doxycycline (malaria prophylaxis)",
  serviceType: "supply",
  minimumAgeYears: 12,
  indicationSummary:
    "Malaria prophylaxis for travel to areas where doxycycline is appropriate for the resistance pattern, as determined by NaTHNaC assessment and current ACMP guidance. Off-label use \u2014 doxycycline is licensed for treating infections; its use for malaria prophylaxis is outside the product licence but is supported by national guidance.",

  brands: [
    {
      brandId: "doxycycline-100mg",
      brandName: "Doxycycline 100 mg",
      minimumAgeYears: 12,
      route: "supply",
      doseDescription: "100 mg (one capsule or tablet, or two 50 mg capsules) once daily",
      administrationNotes:
        "Supply 100 mg capsules or tablets, or 50 mg capsules. Label: one 100 mg dose daily (or two 50 mg capsules if 50 mg strength supplied). Take with a full glass of water. Remain upright for at least 60 minutes after taking the dose \u2014 do not take immediately before lying down or going to bed. Take at the same time each day. Take with or after food to reduce gastrointestinal effects. Dispersible tablets (e.g. Vibramycin-D 100 mg) may be used.",
    },
  ],

  schedules: [
    {
      scheduleId: "doxycycline-daily",
      label:
        "Once daily \u2014 off-label for malaria prophylaxis \u2014 start 1\u20132 days before entering risk area; continue 28 days after leaving",
      offLabel: true,
      offLabelRationale:
        "Doxycycline is licensed for treating bacterial infections. Its use for malaria prophylaxis is outside the product licence and is off-label. This use is supported by national ACMP guidance and is clinically established for prophylaxis in areas with drug-resistant Plasmodium falciparum. Supply requires documented rationale and patient consent to off-label use.",
      doses: [
        {
          doseNumber: 1,
          label: "First dose: 1\u20132 days before entering the malaria-endemic area",
        },
      ],
      completionNotes:
        "Continue once daily throughout the trip. Continue for 28 days after leaving the malaria-endemic area \u2014 do not stop early. Quantity: 1\u20132 days pre-travel + trip duration + 28 days post-travel. Take with a full glass of water and remain upright for at least 60 minutes after each dose to reduce the risk of oesophagitis.",
    },
  ],

  capturesPriorDoseHistory: false,

  supplyGates: [
    "NaTHNaC assessment confirms the destination has malaria risk and doxycycline is appropriate for the resistance pattern of the specific itinerary.",
    "Patient age confirmed as 12 years or over.",
    "Departure date and return date are confirmed and the quantity to supply has been calculated: 1\u20132 days pre-travel + full trip duration + 28 days post-travel.",
    "Patient has been informed that this supply is for an off-label use (malaria prophylaxis) and has given informed consent. This must be documented.",
  ],

  exclusions: [
    {
      checkId: "doxy-ex-age",
      question: "Is the patient under 12 years of age?",
      disqualifyingAnswer: "yes",
      disqualificationMessage:
        "Doxycycline is not indicated for malaria prophylaxis in patients under 12 years of age. Do not supply.",
      referralMessage:
        "Refer to the patient's GP or a specialist travel health clinic for advice on appropriate malaria prophylaxis for children under 12.",
    },
    {
      checkId: "doxy-ex-hypersensitivity",
      question: "Does the patient have known hypersensitivity to doxycycline, any other tetracycline antibiotic, or any excipient of the selected product?",
      disqualifyingAnswer: "yes",
      disqualificationMessage: "Doxycycline is contraindicated in this patient. Do not supply.",
      referralMessage: "Refer to the patient's GP for advice on alternative malaria prophylaxis.",
    },
    {
      checkId: "doxy-ex-pregnancy-breastfeeding",
      question: "Is the patient known or suspected to be pregnant, or breastfeeding?",
      disqualifyingAnswer: "yes",
      disqualificationMessage: "Doxycycline is contraindicated in pregnancy and breastfeeding. Do not supply.",
      referralMessage: "Refer to the patient's GP or obstetrician for advice on malaria prophylaxis.",
    },
    {
      checkId: "doxy-ex-conditions",
      question: "Does the patient have any of the following: systemic lupus erythematosus (SLE), myasthenia gravis, or severe hepatic impairment?",
      disqualifyingAnswer: "yes",
      disqualificationMessage: "Doxycycline is contraindicated in patients with SLE, myasthenia gravis, or severe hepatic impairment. Do not supply.",
      referralMessage: "Refer to the patient's GP for advice on alternative malaria prophylaxis.",
    },
    {
      checkId: "doxy-ex-oesophageal",
      question: "Does the patient have a known oesophageal stricture, oesophageal dysmotility disorder, or active oesophageal disease?",
      disqualifyingAnswer: "yes",
      disqualificationMessage:
        "Oesophageal stricture, dysmotility disorder, or active oesophageal disease is a contraindication to doxycycline due to the risk of oesophagitis and oesophageal ulceration. Do not supply.",
      referralMessage: "Refer to the patient's GP for advice on alternative malaria prophylaxis.",
    },
    {
      checkId: "doxy-ex-fructose-intolerance",
      question: "Has the patient been diagnosed with rare hereditary fructose intolerance, glucose-galactose malabsorption, or sucrase-isomaltase deficiency? (Relevant for product formulations containing these excipients \u2014 check individual SPC.)",
      disqualifyingAnswer: "yes",
      disqualificationMessage: "Some doxycycline formulations contain excipients unsuitable for patients with these rare hereditary conditions. Check the SPC for the specific product selected before supplying.",
      referralMessage: "If no suitable formulation is available, refer to the patient's GP.",
    },
    {
      checkId: "doxy-ex-isotretinoin",
      question: "Is the patient currently taking isotretinoin or any other systemic retinoid?",
      disqualifyingAnswer: "yes",
      disqualificationMessage:
        "Concurrent use of doxycycline and systemic retinoids is contraindicated due to the combined risk of benign intracranial hypertension. Do not supply under this PGD.",
      referralMessage: "Refer to the patient's GP for advice on alternative malaria prophylaxis.",
    },
  ],

  cautions: [
    {
      checkId: "doxy-caut-warfarin",
      question: "Is the patient taking warfarin or another oral anticoagulant?",
      cautionMessage:
        "Tetracyclines including doxycycline depress plasma prothrombin activity and may potentiate the effect of oral anticoagulants. Advise more frequent INR monitoring during the course. Notify the patient's GP of the supply.",
    },
    {
      checkId: "doxy-caut-anticonvulsants",
      question: "Is the patient taking barbiturates, carbamazepine, or phenytoin?",
      cautionMessage:
        "Barbiturates, carbamazepine, and phenytoin may reduce the half-life of doxycycline and decrease its efficacy. Consider an alternative antimalarial or refer to the patient's GP before supply.",
    },
    {
      checkId: "doxy-caut-ciclosporin",
      question: "Is the patient taking ciclosporin?",
      cautionMessage: "Doxycycline may raise ciclosporin plasma levels. Refer to the patient's GP before supply.",
    },
    {
      checkId: "doxy-caut-methoxyflurane",
      question: "Is the patient likely to use methoxyflurane (e.g. Penthrox) as an analgesic during the trip?",
      cautionMessage: "Concurrent use of doxycycline and methoxyflurane carries a risk of renal failure. Refer to the patient's GP before supply.",
    },
    {
      checkId: "doxy-caut-oral-contraceptive",
      question: "Is the patient taking a combined oral contraceptive pill or progestogen-only pill?",
      cautionMessage:
        "Doxycycline may reduce the efficacy of oral contraceptives. Advise the patient to use additional barrier contraception throughout the course and for at least 7 days after stopping doxycycline. Document this advice.",
    },
    {
      checkId: "doxy-caut-antacids-iron",
      question: "Is the patient taking antacids, iron supplements, zinc supplements, or bismuth-containing products?",
      cautionMessage:
        "Antacids, iron, zinc, and bismuth impair the absorption of doxycycline. Advise the patient to separate doses by the maximum possible interval (at least 2\u20133 hours where possible).",
    },
    {
      checkId: "doxy-caut-photosensitivity",
      question: "Has the pharmacist counselled the patient on photosensitivity risk?",
      cautionMessage:
        "Doxycycline commonly causes photosensitivity reactions. Advise the patient to use a high-factor sunscreen (SPF 30 or above), wear protective clothing, and avoid prolonged sun exposure throughout the course. Stop doxycycline at the first sign of skin erythema and seek medical advice.",
    },
    {
      checkId: "doxy-caut-oesophagitis",
      question: "Has the pharmacist counselled the patient on the risk of oesophagitis?",
      cautionMessage:
        "Doxycycline can cause oesophagitis and oesophageal ulceration if it lodges in the oesophagus. Advise the patient to take each dose with a full glass of water and to remain upright for at least 60 minutes afterwards. Do not take immediately before lying down or going to bed.",
    },
  ],

  requiresICVP: false,
  requiresLariamCard: false,
  requiresYellowCardDiscussion: false,
  requiresCHISNotificationUnder19: false,
  requiresGPNotification: true,
  standardObservationMinutes: 0,
  extendedObservationMinutes: 0,

  patientAdvicePoints: [
    "Take one 100 mg dose every day at the same time. Take with a full glass of water and with or after food.",
    "Remain upright (sitting or standing) for at least 60 minutes after taking each dose. Do not take a dose immediately before lying down or going to bed \u2014 this reduces the risk of oesophagitis (inflammation of the gullet).",
    "This medicine is being used outside its product licence (off-label) for malaria prevention. This use is supported by national guidance. You have given your consent to this off-label use.",
    "Start taking doxycycline 1\u20132 days before entering the malaria-endemic area.",
    "Continue taking doxycycline every day throughout your trip and for 28 days after you leave the malaria-endemic area. Do not stop early.",
    "Doxycycline makes your skin more sensitive to sunlight. Use a high-factor sunscreen (SPF 30 or above), wear protective clothing, and avoid prolonged sun exposure. Stop taking it and seek medical advice if you develop a skin reaction.",
    "If you are taking the contraceptive pill: use additional barrier contraception throughout the course and for at least 7 days after stopping doxycycline.",
    "Continue to use mosquito-bite avoidance measures throughout your trip: apply insect repellent containing DEET to exposed skin, wear long-sleeved clothing and long trousers (especially from dusk to dawn), and sleep under a permethrin-treated bed net.",
    "If you develop a fever or flu-like illness at any time during your trip or within 1 year of returning (especially within the first 3 months), seek urgent medical attention immediately and tell the clinician you have been in a malaria-risk area. Malaria can be fatal if not treated promptly.",
  ],
});
